/* eslint-disable max-len */
import React, { useState, useContext } from 'react';
import {
  bool as boolType,
  any,
  shape as shapeType,
  string as stringType,
} from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  bool,
  extend,
  string,
  shape,
  QueryProvider,
  params
} from '@thd-nucleus/data-sources';
import { Carousel } from '@one-thd/sui-carousel';
import classNames from 'classnames';
import { Image } from '@thd-olt-component-react/core-ui';
import { isAppliance } from '../utils/helpers';
import { LoadingPlaceholder } from './LoadingPlaceholder';
import { CarouselHeader } from './CarouselHeader';
import { FallbackCard } from './FallbackCard';
import { RecsProductPod } from './RecsProductPod';
import { IRGUtils } from './IRGUtils';

// the QueryProvider on line 54 doesnt get access to ProductPod because data?.products is empty at first
// this is what hoist() was originally intended for but the problem with hoist() is that it mutates
// components and runs into issues when they are in multiple places
// eslint-disable-next-line react/prop-types
const Wrapper = ({ children }) => children;
Wrapper.dataModel = extend({
  product: params({ itemId: string().isRequired() }).shape({
    dataSource: string(),
  })
}, RecsProductPod);
const ProductPodV7Carousel = (props) => {

  const { channel } = useContext(ExperienceContext);

  const isMobile = channel === 'mobile';

  const {
    anchorImage,
    data,
    hideTitle,
    loading,
    scheme,
    showLoading,
    storeId,
    showFallback,
    fallbackInfo,
    userId,
    subtitle,
    isCategoryTab, // Displaying the category tabs
    hideATC,
    hideFavorites,
    hideBadge,
    membershipInformation,
    zipCode,
    slidesPer,
    keepAddToCart,
    hideSwatches,
    hideCarouselArrows,
    analyticsAnchorProductSku,
    parent,
    zoneName,
    zonePosition,
    podResults,
    brandTitleMaxLine,
    hideRating,
    isIRG,
    noATCFulfillment
  } = props;

  const defaultVariables = {
    storeId,
    zipCode,
    installationServices: false,
    loyaltyMembershipInput: membershipInformation?.data?.loyaltyMembership || null
  };

  const { products, metadata, categories } = data || {};

  const classes = classNames('sui-grid', 'sui-gap-4', 'sui-grid-cols-1', { 'sui-grid-cols-3': anchorImage && !isIRG && !isMobile });

  const colSpanClasses = classNames({ 'sui-col-span-2': anchorImage });

  let title = metadata?.title;

  const [filtered, setFiltered] = useState([]);
  const [isCategorySelected, setIsCategorySelected] = useState(false);

  const pullFilteredData = (filteredProducts) => {
    setIsCategorySelected(true);
    setFiltered(filteredProducts);
  };

  let displayProducts;

  if (isCategorySelected) {
    displayProducts = filtered;
  } else {
    displayProducts = products;
  }

  let breakpoints = ({});
  if (!slidesPer) {
    breakpoints = ({
      sm: {
        slidesPerView: 2,
        slidesPerGroup: 2
      },
      md: {
        slidesPerView: 3,
        slidesPerGroup: 3
      },
      lg: {
        slidesPerView: anchorImage ? 4 : 6,
        slidesPerGroup: anchorImage ? 4 : 6
      },
      xl: {
        slidesPerView: anchorImage ? 4 : 6,
        slidesPerGroup: anchorImage ? 4 : 6
      }
    });
  } else if (slidesPer) {
    breakpoints = ({
      sm: {
        slidesPerView: slidesPer,
        slidesPerGroup: slidesPer
      },
      md: {
        slidesPerView: slidesPer,
        slidesPerGroup: slidesPer
      },
      lg: {
        slidesPerView: anchorImage ? 1 : slidesPer,
        slidesPerGroup: anchorImage ? 1 : slidesPer
      },
      xl: {
        slidesPerView: anchorImage ? 1 : slidesPer,
        slidesPerGroup: anchorImage ? 1 : slidesPer
      }
    });
  }

  let placeholderTxtLines;
  if (!isMobile && isCategoryTab) {
    placeholderTxtLines = 7;
  } else if (!isMobile && anchorImage) {
    placeholderTxtLines = 5;
  } if (zoneName) {
    placeholderTxtLines = 3;
  } else {
    placeholderTxtLines = hideATC ? 4 : 6;
  }

  if ((isIRG && products?.length < 3)) {
    return null;
  }

  return (
    <>
      {showLoading && loading && (
        showFallback && !userId && !products
          ? (
            <FallbackCard
              imageUrl={fallbackInfo.imageUrl}
              header={fallbackInfo.header}
              subheader={fallbackInfo.subheader}
              footerUrl={fallbackInfo.footerUrl}
              footer={fallbackInfo.footer}
            />
          )
          : <LoadingPlaceholder txtPlaceholderLines={placeholderTxtLines} hideTitle={hideTitle} />
      )}
      {displayProducts?.length > 0 && (
        <>
          {!hideTitle && !isIRG && (
            isCategoryTab && !metadata?.collectionName ? (
              <CarouselHeader title="More From This Brand" />
            ) : (
              <CarouselHeader title={title} subtitle={subtitle} />
            )
          )}
          {isIRG && (
            <IRGUtils
              products={products}
              scheme={scheme}
              isIRG={isIRG}
              isCategoryTab={isCategoryTab}
              categories={categories}
              pullFilteredData={pullFilteredData}
            />
          )}

          <div className={classNames(classes, 'sui-pl-2')}>
            {!isMobile && anchorImage && (
              <div>
                <Image
                  src={anchorImage}
                  alt="main-product-image"
                  height="400"
                  width="400"
                  data-testid="anchor-image"
                  className="sui-w-full"
                />
                <div className="sui-bg-strong sui-text-center sui-p-1 sui-mb-4 sui-text-sm">Current Item</div>
              </div>
            )}
            <div className={colSpanClasses}>
              <QueryProvider
                dataSource={scheme}
                cacheKey={`${scheme}-recommendations-carousel`}
                defaultVariables={defaultVariables}
              >
                <Carousel
                  breakpoints={breakpoints}
                  hideCarouselArrows={hideCarouselArrows}
                  disableMargin
                >
                  {displayProducts.map((dynamicProduct, idx) => {
                    const { product } = dynamicProduct;
                    const { itemId } = product;
                    const { preferredPriceFlag } = product.pricing;

                    return (
                      <Wrapper key={idx}>
                        <RecsProductPod
                          itemId={itemId}
                          key={itemId}
                          position={idx}
                          storeId={storeId}
                          scheme={scheme}
                          anchorProduct={analyticsAnchorProductSku}
                          parent={parent}
                          zoneName={zoneName}
                          zonePosition={zonePosition}
                          podResults={podResults}
                          hideSwatches={hideSwatches}
                          hideFavorites={hideFavorites}
                          hideBadge={hideBadge}
                          hideATC={hideATC}
                          hideRating={hideRating}
                          preferredPriceFlag={preferredPriceFlag}
                          brandTitleMaxLine={brandTitleMaxLine}
                          strategy={dynamicProduct?.strategy}
                          noATCFulfillment={isAppliance(product) ? false : noATCFulfillment}
                        />
                      </Wrapper>
                    );
                  }
                  )}
                </Carousel>
              </QueryProvider>
            </div>
          </div>
        </>
      )}
    </>
  );
};

const productModel = extend({}, {
  itemId: string(),
  dataSource: string(),
}, RecsProductPod.dataModel.product);

export const wrappedProduct1 = shape({
  category: string(),
  description: string(),
  idEndDate: string(),
  idPromoDetails: string(),
  idStartDate: string(),
  strategy: string(),
  isAnchor: bool(),
  product: params({
    dataSource: string()
  })
    .shape(productModel),
  slot: shape({
    description: string(),
    isRequired: bool(),
    name: string(),
  })
});
ProductPodV7Carousel.dataModel = extend(Wrapper, wrappedProduct1);

ProductPodV7Carousel.displayName = 'ProductPodV7Carousel';

ProductPodV7Carousel.propTypes = {
  ...RecsProductPod.propTypes,
  // eslint-disable-next-line react/forbid-prop-types
  data: any,
  loading: boolType,
  hideTitle: boolType,
  scheme: stringType,
  showLoading: boolType,
  storeId: stringType.isRequired,
  subtitle: stringType,
  hideATC: boolType,
  hideFavorites: boolType,
  hideBadge: boolType,
  keepAddToCart: boolType,
  isCategoryTab: boolType,
  hideSwatches: boolType,
  hideCarouselArrows: boolType,
  analyticsAnchorProductSku: stringType,
  hideRating: boolType,
  noATCFulfillment: boolType
};

ProductPodV7Carousel.defaultProps = {
  hideTitle: false,
  scheme: 'drecs',
  showLoading: true,
  loading: false,
  data: null,
  subtitle: null,
  hideBadge: false,
  hideATC: false,
  hideFavorites: true,
  keepAddToCart: null,
  isCategoryTab: false,
  hideSwatches: true,
  hideCarouselArrows: false,
  analyticsAnchorProductSku: '',
  hideRating: false,
  noATCFulfillment: true
};

export { ProductPodV7Carousel };
